import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Slide,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import ContentCopy from '@mui/icons-material/ContentCopy';
import OpenInNew from '@mui/icons-material/OpenInNew';
import Refresh from '@mui/icons-material/Refresh';
import Save from '@mui/icons-material/Save';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import CloudUpload from '@mui/icons-material/CloudUpload';
import Close from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { useBeforeUnload, useNavigate } from 'react-router-dom';
import * as api from '../../services/api';
import type { LandingMediaLibraryKind } from '../../services/api';
import { PUBLIC_APP_ORIGIN } from '../../config/api';
import type { LandingConfig } from './landingBuilderTypes';
import LandingPageCanvasEditor, { type BuilderBlock, type BuilderSection } from './LandingPageCanvasEditor';
import CanvasBlockToolbar from './CanvasBlockToolbar';
import CanvasPageToolbar from './CanvasPageToolbar';
import LandingCanvasLeftRail, { LANDING_CANVAS_SCROLL_IDS } from './LandingCanvasLeftRail';
import LandingMediaLibraryPicker from './LandingMediaLibraryPicker';
import { normalizeHex } from './ColorPickerPopover';
import { shapeTextPresetFields, type ShapeTextPreset } from './shapeTextPresets';
import {
  computeCanvasSectionMinHeightPx,
  rescaleBlocksYhPreservingPixelSize,
} from './canvasSectionHeightBlocks';
import { useI18n } from '../../i18n/useI18n';

type CanvasRailPanel = 'library' | 'addButton' | 'addShape' | 'addEventList' | 'addImageSlider';

const CANVAS_RAIL_PANEL_BOX_SX = {
  width: 360,
  flexShrink: 0,
  alignSelf: 'stretch',
  borderRight: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  py: 1.5,
  px: 1.25,
  boxSizing: 'border-box',
} as const;

/** Panel «Xem chi tiết» canvas — giữa rail và preview */
const CANVAS_CHI_TIET_PANEL_BOX_SX = {
  width: 360,
  flexShrink: 0,
  alignSelf: 'stretch',
  borderRight: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  py: 1.5,
  px: 1.25,
  boxSizing: 'border-box',
  minHeight: 0,
  maxHeight: '100%',
  height: '100%',
} as const;

const SECTION_TABS: { value: string; label: string }[] = [
  { value: 'meta', label: 'Meta & SEO' },
  { value: 'hero', label: 'Hero & video' },
  { value: 'partners', label: 'Đối tác' },
  { value: 'frames', label: 'Khung ảnh slide' },
  { value: 'process', label: 'Quy trình VIP' },
  { value: 'pricing', label: 'Photobooth & giá' },
  { value: 'events', label: 'Sự kiện' },
  { value: 'cta', label: 'CTA & footer' },
];

const PREVIEW_SECTIONS: { id: string; label: string }[] = [
  { id: '', label: 'Toàn trang' },
  { id: 'hero', label: 'Hero' },
  { id: 'partners', label: 'Đối tác' },
  { id: 'frames', label: 'Khung ảnh' },
  { id: 'process', label: 'Quy trình' },
  { id: 'pricing', label: 'Giá' },
  { id: 'events', label: 'Sự kiện' },
  { id: 'cta', label: 'CTA' },
];

function clone<T>(o: T): T {
  return JSON.parse(JSON.stringify(o));
}

function genLandingId(prefix: string) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

const LP_MEDIA_TARGET_W_PCT = 44;
/** Ảnh dọc: neo theo h% (tránh neo w 44% → h% đụng trần ~88% = gần full màn). */
const LP_MEDIA_TARGET_H_PCT = 46;
const LP_MEDIA_MIN_W_PCT = 10;
const LP_MEDIA_MAX_W_PCT = 92;
const LP_MEDIA_MIN_H_PCT = 8;
const LP_MEDIA_MAX_H_PCT = 88;
/** Trần h% khi khởi tạo ảnh dọc — đồng bộ LP_MEDIA_AUTOFIT_PORTRAIT_MAX_H_PCT trong public-landing.html */
const LP_MEDIA_PORTRAIT_MAX_H_PCT = 52;
/** Giới hạn số ảnh trong khối dải ảnh — đồng bộ logic thả file trong preview. */
const LP_SLIDER_MAX_IMAGE_SLOTS = 12;

function absLandingAssetUrlForProbe(path: string): string {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const base = PUBLIC_APP_ORIGIN.replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

async function probeLandingMediaIntrinsic(
  url: string,
  probeKind: 'image' | 'video',
): Promise<{ nw: number; nh: number } | null> {
  const abs = absLandingAssetUrlForProbe(url);
  if (!abs) return null;
  if (probeKind === 'video') {
    return new Promise((resolve) => {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.muted = true;
      v.playsInline = true;
      const cleanup = () => {
        v.removeAttribute('src');
        v.load();
      };
      v.onloadedmetadata = () => {
        const vw = v.videoWidth;
        const vh = v.videoHeight;
        cleanup();
        resolve(vw > 0 && vh > 0 ? { nw: vw, nh: vh } : null);
      };
      v.onerror = () => {
        cleanup();
        resolve(null);
      };
      v.src = abs;
    });
  }

  const tryImageOnce = (src: string) =>
    new Promise<{ nw: number; nh: number } | null>((resolve) => {
      const img = new Image();
      img.onload = () => {
        void (async () => {
          try {
            if (typeof img.decode === 'function') await img.decode();
          } catch {
            /* ignore */
          }
          const nw = img.naturalWidth;
          const nh = img.naturalHeight;
          resolve(nw > 0 && nh > 0 ? { nw, nh } : null);
        })();
      };
      img.onerror = () => resolve(null);
      img.src = src;
    });

  let imgProbe = await tryImageOnce(abs);
  if (imgProbe) return imgProbe;
  const bust = abs + (abs.includes('?') ? '&' : '?') + 'lp_probe=' + Date.now();
  imgProbe = await tryImageOnce(bust);
  if (imgProbe) return imgProbe;

  try {
    const res = await fetch(abs, { credentials: 'include' });
    if (res.ok) {
      const blob = await res.blob();
      const bmp = await createImageBitmap(blob);
      const nw = bmp.width;
      const nh = bmp.height;
      bmp.close();
      if (nw > 0 && nh > 0) return { nw, nh };
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** (w%/h%)×(Wpx/Hpx) = nw/nh — khớp tỷ lệ ảnh, không khoảng trống hai bên khi object-fit contain */
function mediaBlockPctFitIntrinsic(
  nw: number,
  nh: number,
  sectionWpx: number,
  sectionHpx: number,
  targetWPct = LP_MEDIA_TARGET_W_PCT,
): { w: number; h: number } {
  if (!(sectionWpx > 0 && sectionHpx > 0)) {
    return { w: 30, h: 30 };
  }
  /** Không đo được bitmap: hộp vuông theo section (tránh fallback 44×28 quá «ngang» → khoảng trống hai bên ảnh đứng). */
  if (!(nw > 0 && nh > 0)) {
    nw = 1;
    nh = 1;
    targetWPct = Math.min(targetWPct, 32);
  }
  const imgAr = nw / nh;
  const secAr = sectionWpx / sectionHpx;
  const portrait = imgAr < 1;
  let w: number;
  let h: number;
  if (portrait) {
    h = LP_MEDIA_TARGET_H_PCT;
    w = (h * imgAr) / secAr;
  } else {
    w = targetWPct;
    h = (w / imgAr) * secAr;
  }

  const maxH = portrait ? LP_MEDIA_PORTRAIT_MAX_H_PCT : LP_MEDIA_MAX_H_PCT;

  const shrinkIfNeeded = () => {
    const sw = w <= LP_MEDIA_MAX_W_PCT ? 1 : LP_MEDIA_MAX_W_PCT / w;
    const sh = h <= maxH ? 1 : maxH / h;
    const s = Math.min(1, sw, sh);
    w *= s;
    h *= s;
  };
  const growIfNeeded = () => {
    const sw = w >= LP_MEDIA_MIN_W_PCT ? 1 : LP_MEDIA_MIN_W_PCT / w;
    const sh = h >= LP_MEDIA_MIN_H_PCT ? 1 : LP_MEDIA_MIN_H_PCT / h;
    const s = Math.max(sw, sh);
    w *= s;
    h *= s;
  };

  shrinkIfNeeded();
  growIfNeeded();
  shrinkIfNeeded();

  return {
    w: Math.round(w * 10) / 10,
    h: Math.round(h * 10) / 10,
  };
}

function waitNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

/**
 * Kích thước pixel của section trong iframe preview (dùng tính % khối media ban đầu).
 * Chiều cao: không được đo thấp hơn sec.height / min-height (px) — nếu không secAr = W/H phình,
 * h% theo intrinsic sẽ chạm trần 100% (ảnh dọc full trang) và kẹt resize.
 * Chiều ngang: chỉ client/offset/rect section — không nới theo docW (nới làm secAr lớn → h% cao thêm).
 */
function readLandingPreviewSectionPx(
  iframeEl: HTMLIFrameElement | null,
  sec: BuilderSection | undefined,
): { w: number; h: number } {
  const hCfg = Math.max(200, sec?.height ?? 480);
  const fallbackW = 520;
  try {
    const doc = iframeEl?.contentWindow?.document;
    if (!doc) {
      return { w: fallbackW, h: hCfg };
    }
    const anchor = sec?.anchorId || sec?.id;
    const sectionEl = anchor ? doc.getElementById(String(anchor)) : null;
    const docW = Math.max(
      120,
      doc.documentElement?.clientWidth || doc.body?.clientWidth || fallbackW,
    );
    if (sectionEl) {
      const r = sectionEl.getBoundingClientRect();
      let w = sectionEl.clientWidth;
      if (!(w > 40)) w = sectionEl.offsetWidth || 0;
      if (!(w > 40)) w = r.width;
      if (!(w > 40)) w = docW;

      let h = sectionEl.clientHeight;
      if (!(h > 40)) h = sectionEl.offsetHeight || 0;
      if (!(h > 40)) h = r.height;
      h = Math.max(h, hCfg);
      const sm = sectionEl.style?.minHeight;
      if (sm && /px$/i.test(String(sm))) {
        const pv = parseFloat(String(sm));
        if (Number.isFinite(pv) && pv > 40) h = Math.max(h, pv);
      }
      h = Math.max(h, r.height || 0, sectionEl.offsetHeight || 0);
      return { w: Math.max(100, w), h: Math.max(100, h) };
    }
    return { w: Math.max(100, docW), h: hCfg };
  } catch {
    return { w: fallbackW, h: hCfg };
  }
}

function ensureLandingBuilder(d: LandingConfig): { mode?: string; sections: BuilderSection[] } {
  if (!d.builder || typeof d.builder !== 'object') {
    d.builder = { mode: 'canvas', sections: [] };
  }
  const b = d.builder as Record<string, unknown>;
  if (!Array.isArray(b.sections)) b.sections = [];
  return b as { mode?: string; sections: BuilderSection[] };
}

const AdminLandingPage: React.FC = () => {
  const { admin: a, pages: t } = useI18n();
  const publicUrl = `${PUBLIC_APP_ORIGIN}/public/landing`;
  const [config, setConfig] = useState<LandingConfig>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settingsTab, setSettingsTab] = useState('hero');
  const [previewSection, setPreviewSection] = useState('');
  const [iframeKey, setIframeKey] = useState(0);
  const [previewPagesMode, setPreviewPagesMode] = useState(false);
  const [editorMode, setEditorMode] = useState<'legacy' | 'canvas'>('legacy');
  const [canvasSelectedSectionId, setCanvasSelectedSectionId] = useState<string | null>(null);
  const [canvasSelectedBlockId, setCanvasSelectedBlockId] = useState<string | null>(null);
  const [canvasRailHighlightId, setCanvasRailHighlightId] = useState<string | null>(null);
  const [canvasRailPanel, setCanvasRailPanel] = useState<CanvasRailPanel | null>(null);
  const [canvasChiTietOpen, setCanvasChiTietOpen] = useState(false);
  /** Dialog chọn ảnh thư viện khi bấm + trên dải ảnh trong preview. */
  const [sliderSlotDialog, setSliderSlotDialog] = useState<{
    sectionId: string;
    blockId: string;
    slotIndex: number;
  } | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [leavePromptOpen, setLeavePromptOpen] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  /** Sao chép phần tử canvas từ preview (postMessage) — dán trong cùng trang. */
  const landingBlockClipboardJsonRef = useRef<string | null>(null);
  /** Preview: bấm ô slide → mở thư viện; lần chọn/tải ảnh kế tiếp ghi vào `images[slotIndex]`. */
  const landingSliderSlotPickRef = useRef<{
    sectionId: string;
    blockId: string;
    slotIndex: number;
  } | null>(null);
  const uploadLandingAssetRef = useRef<(file: File) => Promise<string | null>>(async () => null);
  const undoPastRef = useRef<LandingConfig[]>([]);
  const undoFutureRef = useRef<LandingConfig[]>([]);
  const pendingNavigationRef = useRef<string | 'pop' | null>(null);
  /** Đã push thêm 1 entry guard để bắt nút Back (cùng URL) */
  const historyGuardPushedRef = useRef(false);
  /** Bỏ qua 1 lần popstate (khi gỡ guard sau khi lưu) */
  const skipPopstateRef = useRef(false);
  /** Bỏ qua N lần popstate (forward/go sau dialog — tránh mở lại dialog; có thể >1 sự kiện) */
  const suppressLeavePopstateCountRef = useRef(0);

  const navigate = useNavigate();

  const applyFullConfig = useCallback((c: LandingConfig) => {
    setConfig(c);
    const m = (c.builder as { mode?: string } | undefined)?.mode;
    setEditorMode(m === 'canvas' ? 'canvas' : 'legacy');
  }, []);

  /** Chuỗi JSON giống payload gửi khi lưu — để biết có thay đổi so với lần tải/lưu cuối */
  const buildPersistSnapshot = useCallback((cfg: LandingConfig, mode: 'legacy' | 'canvas') => {
    const body = clone(cfg) as LandingConfig;
    if (!body.builder || typeof body.builder !== 'object') body.builder = {};
    const bm = body.builder as Record<string, unknown>;
    bm.mode = mode === 'canvas' ? 'canvas' : 'legacy';
    return JSON.stringify(body);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getLandingPageAdminConfig();
      const loaded = (data.config ?? {}) as LandingConfig;
      applyFullConfig(loaded);
      const mode =
        (loaded.builder as { mode?: string } | undefined)?.mode === 'canvas' ? 'canvas' : 'legacy';
      setSavedSnapshot(buildPersistSnapshot(loaded, mode));
      undoPastRef.current = [];
      undoFutureRef.current = [];
    } catch (e) {
      console.error(e);
      toast.error('Không tải được cấu hình landing page');
      setSavedSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, [applyFullConfig, buildPersistSnapshot]);

  const currentSnapshot = useMemo(
    () => buildPersistSnapshot(config, editorMode),
    [buildPersistSnapshot, config, editorMode],
  );

  const isDirty = useMemo(
    () => savedSnapshot !== null && currentSnapshot !== savedSnapshot,
    [savedSnapshot, currentSnapshot],
  );

  /** Khi có thay đổi chưa lưu: thêm 1 entry history trùng URL — Back lần 1 chạm guard và mở dialog thay vì phải bấm 2 lần */
  useEffect(() => {
    if (!isDirty || loading || savedSnapshot === null) return;
    if (historyGuardPushedRef.current) return;
    window.history.pushState({ __lplUnsavedGuard: true }, '', window.location.href);
    historyGuardPushedRef.current = true;
  }, [isDirty, loading, savedSnapshot]);

  /** Đã lưu / khôi phục đồng bộ với server: gỡ entry guard để Back không phải bấm 2 lần */
  useEffect(() => {
    if (isDirty || !historyGuardPushedRef.current) return;
    historyGuardPushedRef.current = false;
    skipPopstateRef.current = true;
    window.history.back();
  }, [isDirty]);

  /** Nút Back của trình duyệt (đã tiêu guard → hỏi lưu) */
  useEffect(() => {
    const onPopState = () => {
      if (skipPopstateRef.current) {
        skipPopstateRef.current = false;
        return;
      }
      if (suppressLeavePopstateCountRef.current > 0) {
        suppressLeavePopstateCountRef.current -= 1;
        return;
      }
      if (!isDirty) return;
      historyGuardPushedRef.current = false;
      pendingNavigationRef.current = 'pop';
      setLeavePromptOpen(true);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isDirty]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (editorMode !== 'canvas') setPreviewPagesMode(false);
  }, [editorMode]);

  useEffect(() => {
    if (editorMode !== 'canvas') setCanvasRailPanel(null);
    if (editorMode !== 'canvas') setCanvasChiTietOpen(false);
  }, [editorMode]);

  useEffect(() => {
    if (!canvasSelectedSectionId) setCanvasRailPanel(null);
  }, [canvasSelectedSectionId]);

  const previewSrc = useMemo(() => {
    const u = new URL(`${PUBLIC_APP_ORIGIN}/public/landing`);
    u.searchParams.set('preview', '1');
    if (previewSection) u.searchParams.set('section', previewSection);
    if (previewPagesMode) u.searchParams.set('previewPages', '1');
    return u.toString();
  }, [previewSection, previewPagesMode]);

  const applyWithUndo = useCallback((mutate: (d: LandingConfig) => void) => {
    setConfig((prev) => {
      undoPastRef.current.push(clone(prev));
      if (undoPastRef.current.length > 80) undoPastRef.current.shift();
      undoFutureRef.current = [];
      const next = clone(prev);
      mutate(next);
      return next;
    });
  }, []);

  /** Cùng stack Undo với Ctrl+Z / Ctrl+Y — toolbar canvas, sidebar, v.v. */
  const patch = applyWithUndo;

  const undo = useCallback(() => {
    setConfig((current) => {
      const past = undoPastRef.current;
      if (!past.length) return current;
      const prevSnap = past.pop()!;
      undoFutureRef.current.push(clone(current));
      return prevSnap;
    });
  }, []);

  const redo = useCallback(() => {
    setConfig((current) => {
      const fut = undoFutureRef.current;
      if (!fut.length) return current;
      const nextSnap = fut.pop()!;
      undoPastRef.current.push(clone(current));
      return nextSnap;
    });
  }, []);

  const deleteCanvasBlock = useCallback(
    (sectionId: string, blockId: string) => {
      applyWithUndo((cfg) => {
        const b = ensureLandingBuilder(cfg);
        const sec = b.sections.find((s) => s.id === sectionId);
        if (!sec) return;
        sec.blocks = sec.blocks.filter((bl) => bl.id !== blockId);
      });
      setCanvasSelectedBlockId(null);
    },
    [applyWithUndo],
  );

  const builderSections = useMemo(() => {
    const b = config.builder as { sections?: BuilderSection[] } | undefined;
    const secs = b?.sections;
    return Array.isArray(secs) ? secs : [];
  }, [config.builder]);

  /** Legacy = anchor cố định; Canvas = chỉ các trang đã tạo trong builder */
  const previewSectionOptions = useMemo(() => {
    if (editorMode === 'canvas') {
      const rows = builderSections.map((s, i) => ({
        id: s.anchorId || s.id,
        label: (s.name && String(s.name).trim()) || `Trang ${i + 1}`,
      }));
      return [{ id: '', label: 'Toàn trang' }, ...rows];
    }
    return PREVIEW_SECTIONS;
  }, [editorMode, builderSections]);

  useEffect(() => {
    const valid = new Set(previewSectionOptions.map((o) => o.id));
    if (!valid.has(previewSection)) {
      setPreviewSection('');
    }
  }, [previewSectionOptions, previewSection]);

  useEffect(() => {
    if (!builderSections.length) {
      setCanvasSelectedSectionId(null);
      setCanvasSelectedBlockId(null);
      return;
    }
    setCanvasSelectedSectionId((prev) => {
      if (prev && builderSections.some((s) => s.id === prev)) return prev;
      return builderSections[0].id;
    });
  }, [builderSections]);

  const previewToolbarBlock = useMemo(() => {
    if (!canvasSelectedSectionId || !canvasSelectedBlockId) return null;
    const sec = builderSections.find((s) => s.id === canvasSelectedSectionId);
    return sec?.blocks.find((b) => b.id === canvasSelectedBlockId) ?? null;
  }, [builderSections, canvasSelectedSectionId, canvasSelectedBlockId]);

  const applyCanvasLibraryAsset = useCallback(
    async (
      url: string,
      libKind: LandingMediaLibraryKind,
      opts?: {
        alwaysAdd?: boolean;
        sectionId?: string;
        dropXPct?: number;
        dropYPct?: number;
      },
    ) => {
      const mk: 'image' | 'video' = libKind === 'video' ? 'video' : 'image';
      const alwaysAdd = opts?.alwaysAdd === true;

      const slotPick = landingSliderSlotPickRef.current;
      if (slotPick) {
        if (mk === 'video') {
          landingSliderSlotPickRef.current = null;
          toast.info('Dải ảnh chỉ nhận ảnh — hãy chọn ảnh hoặc GIF trong thư viện.');
          return;
        }
        const { sectionId, blockId, slotIndex } = slotPick;
        landingSliderSlotPickRef.current = null;
        patch((cfg) => {
          const b = ensureLandingBuilder(cfg);
          const sec = b.sections.find((s) => s.id === sectionId);
          const blk = sec?.blocks.find((x) => x.id === blockId);
          if (!blk || blk.type !== 'imageSlider') return;
          if (!Array.isArray(blk.images)) blk.images = [];
          const arr = blk.images;
          while (arr.length <= slotIndex) arr.push('');
          arr[slotIndex] = url.trim();
          while (arr.length && !String(arr[arr.length - 1] ?? '').trim()) arr.pop();
        });
        setCanvasSelectedSectionId(sectionId);
        setCanvasSelectedBlockId(blockId);
        return;
      }

      if (
        !alwaysAdd &&
        previewToolbarBlock?.type === 'media' &&
        canvasSelectedBlockId
      ) {
        patch((d) => {
          const b = ensureLandingBuilder(d);
          b.mode = 'canvas';
          const sec = b.sections.find((s) => s.id === canvasSelectedSectionId);
          if (!sec) return;
          const blk = sec.blocks.find((x) => x.id === canvasSelectedBlockId);
          if (blk?.type === 'media') {
            blk.src = url;
            blk.mediaKind = mk;
          }
        });
        return;
      }
      if (
        !alwaysAdd &&
        previewToolbarBlock?.type === 'imageSlider' &&
        canvasSelectedBlockId &&
        mk === 'image'
      ) {
        patch((d) => {
          const b = ensureLandingBuilder(d);
          b.mode = 'canvas';
          const sec = b.sections.find((s) => s.id === canvasSelectedSectionId);
          if (!sec) return;
          const blk = sec.blocks.find((x) => x.id === canvasSelectedBlockId);
          if (blk?.type === 'imageSlider') {
            if (!Array.isArray(blk.images)) blk.images = [];
            blk.images.push(url);
          }
        });
        return;
      }
      const targetSectionId =
        typeof opts?.sectionId === 'string' && builderSections.some((s) => s.id === opts.sectionId)
          ? opts.sectionId
          : canvasSelectedSectionId;
      if (!targetSectionId) return;

      await waitNextPaint();

      const secRow = builderSections.find((s) => s.id === targetSectionId);
      if (!secRow) return;
      const { w: secWpx, h: secHpx } = readLandingPreviewSectionPx(iframeRef.current, secRow);

      let wPct = LP_MEDIA_TARGET_W_PCT;
      let hPct = 28;

      const probeKind: 'image' | 'video' = mk === 'video' ? 'video' : 'image';
      const probe = await probeLandingMediaIntrinsic(url, probeKind);
      if (probe) {
        const box = mediaBlockPctFitIntrinsic(probe.nw, probe.nh, secWpx, secHpx, LP_MEDIA_TARGET_W_PCT);
        wPct = box.w;
        hPct = box.h;
      }

      const hasDrop =
        typeof opts?.dropXPct === 'number' &&
        Number.isFinite(opts.dropXPct) &&
        typeof opts?.dropYPct === 'number' &&
        Number.isFinite(opts.dropYPct);
      let xPct = Math.round(Math.max(2, Math.min(98 - wPct, (100 - wPct) / 2)) * 10) / 10;
      let yPct = 18;
      if (hasDrop) {
        const cx = Math.max(wPct / 2, Math.min(100 - wPct / 2, opts!.dropXPct!));
        const cy = Math.max(hPct / 2, Math.min(100 - hPct / 2, opts!.dropYPct!));
        xPct = Math.round((cx - wPct / 2) * 10) / 10;
        yPct = Math.round((cy - hPct / 2) * 10) / 10;
        if (xPct < 0) xPct = 0;
        if (xPct + wPct > 100) xPct = Math.round((100 - wPct) * 10) / 10;
        if (yPct < 0) yPct = 0;
        if (yPct + hPct > 100) yPct = Math.round((100 - hPct) * 10) / 10;
      }

      const id = genLandingId('blk');
      patch((d) => {
        const b = ensureLandingBuilder(d);
        b.mode = 'canvas';
        const sec = b.sections.find((s) => s.id === targetSectionId);
        if (!sec) return;
        sec.blocks.push({
          id,
          type: 'media',
          x: xPct,
          y: yPct,
          w: wPct,
          h: hPct,
          mediaKind: mk,
          src: url,
          poster: '',
          alt: '',
          style: {
            borderRadius: 16,
            borderWidth: 0,
            borderColor: '#ec4899',
            borderStyle: 'solid',
            backgroundColor: '#0f172a',
            opacity: 1,
            objectFit: 'contain',
          },
        });
      });
      setCanvasSelectedSectionId(targetSectionId);
      setCanvasSelectedBlockId(id);
    },
    [
      builderSections,
      canvasSelectedBlockId,
      canvasSelectedSectionId,
      patch,
      previewToolbarBlock,
    ],
  );

  const noopLibraryApply = useCallback(async () => {}, []);

  const applySliderSlotLibraryMulti = useCallback(
    (picked: Array<{ url: string; kind: LandingMediaLibraryKind }>) => {
      const dlg = sliderSlotDialog;
      if (!dlg) return;
      const urls = picked
        .filter((p) => p.kind !== 'video')
        .map((p) => p.url.trim())
        .filter(Boolean);
      if (!urls.length) {
        toast.info('Dải ảnh chỉ nhận ảnh hoặc GIF.');
        return;
      }
      const { sectionId, blockId, slotIndex } = dlg;
      applyWithUndo((cfg) => {
        const b = ensureLandingBuilder(cfg);
        const sec = b.sections.find((s) => s.id === sectionId);
        const blk = sec?.blocks.find((x) => x.id === blockId);
        if (!blk || blk.type !== 'imageSlider') return;
        const arr: string[] = Array.isArray(blk.images) ? [...blk.images] : [];
        const countNonEmpty = (a: string[]) =>
          a.reduce((n, u) => n + (String(u ?? '').trim() ? 1 : 0), 0);
        let c = countNonEmpty(arr);
        const assigned: string[] = [];
        for (const u of urls) {
          const writeAt = slotIndex + assigned.length;
          while (arr.length <= writeAt) arr.push('');
          const was = String(arr[writeAt] ?? '').trim() ? 1 : 0;
          if (c - was + 1 > LP_SLIDER_MAX_IMAGE_SLOTS) break;
          arr[writeAt] = u;
          assigned.push(u);
          c = countNonEmpty(arr);
        }
        while (arr.length && !String(arr[arr.length - 1] ?? '').trim()) arr.pop();
        blk.images = arr;
      });
      landingSliderSlotPickRef.current = null;
      setSliderSlotDialog(null);
      setCanvasSelectedSectionId(sectionId);
      setCanvasSelectedBlockId(blockId);
    },
    [sliderSlotDialog, applyWithUndo],
  );

  const uploadLandingAsset = useCallback(async (file: File): Promise<string | null> => {
    setUploading(true);
    try {
      const res = await api.uploadLandingPageAsset(file);
      if (res.url) {
        toast.success('Đã upload file');
        return res.url;
      }
      return null;
    } catch (e) {
      console.error(e);
      toast.error('Upload thất bại');
      return null;
    } finally {
      setUploading(false);
    }
  }, []);

  useEffect(() => {
    uploadLandingAssetRef.current = uploadLandingAsset;
  }, [uploadLandingAsset]);

  /** postMessage vào iframe preview (canvas) — dùng chung cho arm kéo thư viện + thả. */
  const postPreviewCanvasIframeMessage = useCallback(
    (payload: Record<string, unknown>) => {
      if (editorMode !== 'canvas') return;
      const iframe = iframeRef.current;
      const cw = iframe?.contentWindow;
      if (!iframe || !cw) return;
      let targetOrigin: string;
      try {
        const src =
          iframe.src && iframe.src !== 'about:blank' ? iframe.src : previewSrc;
        targetOrigin = new URL(src).origin;
      } catch {
        targetOrigin = new URL(PUBLIC_APP_ORIGIN).origin;
      }
      try {
        cw.postMessage({ source: 'mphoto-admin', ...payload }, targetOrigin);
      } catch {
        /* ignore */
      }
    },
    [editorMode, previewSrc],
  );

  const postLibraryDragArmToPreview = useCallback(
    (armed: boolean) => {
      postPreviewCanvasIframeMessage({ type: 'lp-library-drag-arm', armed });
    },
    [postPreviewCanvasIframeMessage],
  );

  /** Màu chữ toolbar: iframe tô đoạn nếu đang bôi đen trong preview; không thì đổi màu mặc định cả khung. */
  const requestShapeTextToolbarColor = useCallback(
    (p: { sectionId: string; blockId: string; hex: string }) => {
      const cw = iframeRef.current?.contentWindow;
      if (editorMode === 'canvas' && cw) {
        postPreviewCanvasIframeMessage({
          type: 'lp-shape-text-toolbar-color',
          sectionId: p.sectionId,
          blockId: p.blockId,
          hex: p.hex,
        });
        return;
      }
      patch((d) => {
        const b = ensureLandingBuilder(d);
        const sec = b.sections.find((s) => s.id === p.sectionId);
        const blk = sec?.blocks.find((x) => x.id === p.blockId);
        if (blk && blk.type === 'shapeText') blk.style.color = normalizeHex(p.hex);
      });
    },
    [editorMode, patch, postPreviewCanvasIframeMessage],
  );

  /** Edge: tọa độ drag trên parent lệch — iframe tự hit-test bằng dragover/pointermove khi đã arm. */
  const handleLibraryDragReleaseOverPreview = useCallback(
    (info: { clientX: number; clientY: number; url: string; kind: LandingMediaLibraryKind }) => {
      const cx = Number(info.clientX);
      const cy = Number(info.clientY);
      const body: Record<string, unknown> = {
        type: 'lp-library-drag-end',
        url: info.url,
        kind: info.kind,
      };
      if (Number.isFinite(cx) && Number.isFinite(cy)) {
        body.clientX = cx;
        body.clientY = cy;
      }
      postPreviewCanvasIframeMessage(body);
    },
    [postPreviewCanvasIframeMessage],
  );

  const toggleCanvasChiTietPanel = useCallback(() => {
    setCanvasChiTietOpen((open) => {
      if (open) {
        setCanvasRailHighlightId(null);
        return false;
      }
      setCanvasRailPanel(null);
      setCanvasRailHighlightId(LANDING_CANVAS_SCROLL_IDS.detail);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.getElementById(LANDING_CANVAS_SCROLL_IDS.page)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        });
      });
      return true;
    });
  }, []);

  const closeCanvasChiTietPanel = useCallback(() => {
    setCanvasChiTietOpen(false);
    setCanvasRailHighlightId(null);
  }, []);

  const addCanvasBlock = useCallback(
    (kind: 'button' | 'shapeText' | 'eventList' | 'imageSlider', shapePreset?: ShapeTextPreset) => {
      if (!canvasSelectedSectionId) return;
      const id = genLandingId('blk');
      patch((d) => {
        const b = ensureLandingBuilder(d);
        b.mode = 'canvas';
        const sec = b.sections.find((s) => s.id === canvasSelectedSectionId);
        if (!sec) return;
        if (kind === 'button') {
          sec.blocks.push({
            id,
            type: 'button',
            x: 30,
            y: 40,
            w: 32,
            h: 10,
            label: 'Nút',
            actionType: 'scroll',
            scrollTarget: 'landing-section-pricing',
            style: {
              backgroundColor: '#f97316',
              color: '#ffffff',
              borderRadius: 100,
              fontSize: 16,
              borderWidth: 0,
              borderColor: '#000000',
            },
          });
        } else if (kind === 'shapeText') {
          sec.blocks.push({
            id,
            ...shapeTextPresetFields(shapePreset ?? 'body'),
          });
        } else if (kind === 'imageSlider') {
          sec.blocks.push({
            id,
            type: 'imageSlider',
            x: 8,
            y: 18,
            w: 84,
            h: 50,
            images: [],
            scrollDirection: 'ltr',
            scrollSpeedSec: 24,
            autoplaySec: 0,
            style: {
              borderRadius: 16,
              borderWidth: 0,
              borderColor: '#ec4899',
              borderStyle: 'solid',
              backgroundColor: '#0f172a',
              opacity: 1,
              objectFit: 'contain',
            },
          });
        } else {
          sec.blocks.push({
            id,
            type: 'eventList',
            x: 5,
            y: 50,
            w: 90,
            h: 40,
            batchSize: 6,
          });
        }
      });
      setCanvasSelectedBlockId(id);
    },
    [canvasSelectedSectionId, patch],
  );

  useEffect(() => {
    if (!canvasSelectedBlockId || !canvasSelectedSectionId) return;
    const sec = builderSections.find((s) => s.id === canvasSelectedSectionId);
    if (!sec?.blocks.some((b) => b.id === canvasSelectedBlockId)) {
      setCanvasSelectedBlockId(null);
    }
  }, [builderSections, canvasSelectedSectionId, canvasSelectedBlockId]);

  /** Click block trên preview luôn gắn đúng trang chứa block → panel «Chỉnh chi tiết trang» khớp */
  useEffect(() => {
    if (!canvasSelectedBlockId) return;
    const sec = builderSections.find((s) => s.blocks.some((b) => b.id === canvasSelectedBlockId));
    if (sec && sec.id !== canvasSelectedSectionId) {
      setCanvasSelectedSectionId(sec.id);
    }
  }, [builderSections, canvasSelectedBlockId, canvasSelectedSectionId]);

  useEffect(() => {
    const p = landingSliderSlotPickRef.current;
    if (
      p &&
      (canvasSelectedBlockId !== p.blockId || canvasSelectedSectionId !== p.sectionId)
    ) {
      landingSliderSlotPickRef.current = null;
    }
    setSliderSlotDialog((prev) => {
      if (!prev) return prev;
      if (
        canvasSelectedBlockId === prev.blockId &&
        canvasSelectedSectionId === prev.sectionId
      ) {
        return prev;
      }
      landingSliderSlotPickRef.current = null;
      return null;
    });
  }, [canvasSelectedBlockId, canvasSelectedSectionId]);

  const pushPreviewToIframe = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    try {
      win.postMessage(
        {
          source: 'mphoto-admin',
          type: 'landing-config',
          config,
          selection: canvasSelectedSectionId
            ? {
                sectionId: canvasSelectedSectionId,
                blockId: canvasSelectedBlockId,
              }
            : null,
        },
        new URL(PUBLIC_APP_ORIGIN).origin,
      );
    } catch (_) {
      /* ignore */
    }
  }, [config, canvasSelectedSectionId, canvasSelectedBlockId]);

  useEffect(() => {
    pushPreviewToIframe();
  }, [pushPreviewToIframe]);

  useEffect(() => {
    if (editorMode === 'canvas') setPreviewPagesMode(true);
  }, [editorMode]);

  useEffect(() => {
    const allowedOrigin = new URL(PUBLIC_APP_ORIGIN).origin;
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== allowedOrigin) return;
      const d = e.data as {
        source?: string;
        action?: string;
        sectionId?: string;
        blockId?: string;
        mode?: string;
        afterBlockId?: string;
        x?: number;
        y?: number;
        w?: number;
        h?: number;
        html?: string;
        plainFallback?: string;
        height?: number;
        name?: string;
        skipUndo?: boolean;
        url?: string;
        kind?: string;
        dropXPct?: number;
        dropYPct?: number;
        slotIndex?: number;
        direction?: string;
        scrollDirection?: string;
        deltaSec?: number;
        scrollSpeedSec?: number;
      };
      if (!d || d.source !== 'mphoto-landing-builder') return;

      switch (d.action) {
        case 'requestPickSliderSlot': {
          const sid = typeof d.sectionId === 'string' ? d.sectionId : '';
          const bid = typeof d.blockId === 'string' ? d.blockId : '';
          const rawSi = Number(d.slotIndex);
          const slotIndex = Number.isFinite(rawSi) ? Math.max(0, Math.floor(rawSi)) : -1;
          if (!sid || !bid || slotIndex < 0 || slotIndex > 24) break;
          landingSliderSlotPickRef.current = { sectionId: sid, blockId: bid, slotIndex };
          setCanvasSelectedSectionId(sid);
          setCanvasSelectedBlockId(bid);
          setSliderSlotDialog({ sectionId: sid, blockId: bid, slotIndex });
          break;
        }
        case 'removeSliderImageAt': {
          const sid = typeof d.sectionId === 'string' ? d.sectionId : '';
          const bid = typeof d.blockId === 'string' ? d.blockId : '';
          const rawSi = Number(d.slotIndex);
          const slotIndex = Number.isFinite(rawSi) ? Math.max(0, Math.floor(rawSi)) : -1;
          if (!sid || !bid || slotIndex < 0 || slotIndex > 24) break;
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            const sec = b.sections.find((s) => s.id === sid);
            const blk = sec?.blocks.find((x) => x.id === bid);
            if (!blk || blk.type !== 'imageSlider' || !Array.isArray(blk.images)) return;
            const arr = blk.images;
            if (slotIndex >= arr.length) return;
            arr.splice(slotIndex, 1);
            while (arr.length && !String(arr[arr.length - 1] ?? '').trim()) arr.pop();
          });
          toast.success('Đã xóa ảnh khỏi dải');
          setCanvasSelectedSectionId(sid);
          setCanvasSelectedBlockId(bid);
          break;
        }
        case 'setSliderScrollDirection': {
          const sid = typeof d.sectionId === 'string' ? d.sectionId : '';
          const bid = typeof d.blockId === 'string' ? d.blockId : '';
          const sd = typeof (d as { scrollDirection?: string }).scrollDirection === 'string'
            ? String((d as { scrollDirection?: string }).scrollDirection)
            : '';
          const dir = sd === 'rtl' ? 'rtl' : 'ltr';
          if (!sid || !bid) break;
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            const sec = b.sections.find((s) => s.id === sid);
            const blk = sec?.blocks.find((x) => x.id === bid);
            if (!blk || blk.type !== 'imageSlider') return;
            blk.scrollDirection = dir;
          });
          break;
        }
        case 'adjustSliderScrollSpeed': {
          const sid = typeof d.sectionId === 'string' ? d.sectionId : '';
          const bid = typeof d.blockId === 'string' ? d.blockId : '';
          const delta = Number((d as { deltaSec?: number }).deltaSec);
          if (!sid || !bid || !Number.isFinite(delta)) break;
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            const sec = b.sections.find((s) => s.id === sid);
            const blk = sec?.blocks.find((x) => x.id === bid);
            if (!blk || blk.type !== 'imageSlider') return;
            const cur = Number(blk.scrollSpeedSec);
            const base = Number.isFinite(cur) && cur >= 4 ? cur : 24;
            blk.scrollSpeedSec = Math.max(4, Math.min(120, Math.round((base + delta) * 10) / 10));
          });
          break;
        }
        case 'setSliderScrollSpeed': {
          const sid = typeof d.sectionId === 'string' ? d.sectionId : '';
          const bid = typeof d.blockId === 'string' ? d.blockId : '';
          const raw = Number(d.scrollSpeedSec);
          if (!sid || !bid || !Number.isFinite(raw)) break;
          const v = Math.max(4, Math.min(120, Math.round(raw)));
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            const sec = b.sections.find((s) => s.id === sid);
            const blk = sec?.blocks.find((x) => x.id === bid);
            if (!blk || blk.type !== 'imageSlider') return;
            blk.scrollSpeedSec = v;
          });
          break;
        }
        case 'sliderDropLocalFiles': {
          const sid = typeof d.sectionId === 'string' ? d.sectionId : '';
          const bid = typeof d.blockId === 'string' ? d.blockId : '';
          const rawFiles = (d as { files?: unknown }).files;
          const arr = Array.isArray(rawFiles) ? rawFiles : [];
          const files = arr.filter((x): x is File => x instanceof File);
          if (!sid || !bid || !files.length) break;
          const imgs = files.filter((f) => f.type.startsWith('image/'));
          if (!imgs.length) break;
          landingSliderSlotPickRef.current = null;
          setSliderSlotDialog(null);
          setCanvasSelectedSectionId(sid);
          setCanvasSelectedBlockId(bid);
          const secRow = builderSections.find((s) => s.id === sid);
          const blkRow = secRow?.blocks.find((x) => x.id === bid);
          let existing = 0;
          if (blkRow?.type === 'imageSlider' && Array.isArray(blkRow.images)) {
            existing = blkRow.images.filter((u) => String(u || '').trim()).length;
          }
          const room = Math.max(0, LP_SLIDER_MAX_IMAGE_SLOTS - existing);
          const slice = imgs.slice(0, room);
          void (async () => {
            const urls: string[] = [];
            for (const f of slice) {
              const url = await uploadLandingAssetRef.current(f);
              if (url) urls.push(url.trim());
            }
            if (!urls.length) return;
            applyWithUndo((cfg) => {
              const b = ensureLandingBuilder(cfg);
              const s2 = b.sections.find((s) => s.id === sid);
              const b2 = s2?.blocks.find((x) => x.id === bid);
              if (!b2 || b2.type !== 'imageSlider') return;
              if (!Array.isArray(b2.images)) b2.images = [];
              let ne = b2.images.filter((u) => String(u || '').trim()).length;
              for (let ui = 0; ui < urls.length && ne < LP_SLIDER_MAX_IMAGE_SLOTS; ui++) {
                b2.images.push(urls[ui]);
                ne++;
              }
            });
          })();
          break;
        }
        case 'selectSection':
          if (d.sectionId) setCanvasSelectedSectionId(d.sectionId);
          setCanvasSelectedBlockId(null);
          break;
        case 'renameSection': {
          const nm = typeof d.name === 'string' ? d.name.trim() : '';
          if (!d.sectionId || !nm) break;
          const name = nm.slice(0, 200);
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            const sec = b.sections.find((s) => s.id === d.sectionId);
            if (sec) sec.name = name;
          });
          break;
        }
        case 'setSectionHeight': {
          const h = Number(d.height);
          if (!d.sectionId || Number.isNaN(h)) break;
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            const sec = b.sections.find((s) => s.id === d.sectionId);
            if (!sec) return;
            const oldH = Math.max(200, sec.height ?? 480);
            const minH = computeCanvasSectionMinHeightPx(sec.blocks, oldH);
            let targetH = Math.min(20000, Math.max(200, Math.round(h)));
            targetH = Math.max(minH, targetH);
            if (oldH === targetH) {
              sec.height = targetH;
              return;
            }
            sec.height = targetH;
            rescaleBlocksYhPreservingPixelSize(sec.blocks, oldH, targetH);
          });
          break;
        }
        case 'moveSection': {
          const dir = d.direction === 'up' ? -1 : 1;
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            const arr = b.sections;
            const i = arr.findIndex((s) => s.id === d.sectionId);
            if (i < 0) return;
            const j = i + dir;
            if (j < 0 || j >= arr.length) return;
            const t = arr[i];
            arr[i] = arr[j];
            arr[j] = t;
          });
          break;
        }
        case 'deleteSection': {
          if (!d.sectionId) break;
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            const arr = b.sections;
            if (arr.length <= 1) return;
            b.sections = arr.filter((s) => s.id !== d.sectionId);
          });
          setCanvasSelectedSectionId((prev) => (prev === d.sectionId ? null : prev));
          break;
        }
        case 'duplicateSection': {
          if (!d.sectionId) break;
          let newId = '';
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            const arr = b.sections;
            const i = arr.findIndex((s) => s.id === d.sectionId);
            if (i < 0) return;
            const raw = JSON.parse(JSON.stringify(arr[i])) as BuilderSection;
            newId = genLandingId('sec');
            raw.id = newId;
            raw.anchorId = newId;
            raw.name = `${raw.name || 'Khu vực'} (bản sao)`;
            raw.blocks = (raw.blocks || []).map((blk: BuilderBlock) => {
              const bid = genLandingId('blk');
              const bc = JSON.parse(JSON.stringify(blk)) as BuilderBlock;
              bc.id = bid;
              return bc;
            });
            arr.splice(i + 1, 0, raw);
          });
          if (newId) setCanvasSelectedSectionId(newId);
          break;
        }
        case 'addSectionAfter': {
          if (!d.sectionId) break;
          const newSecId = genLandingId('sec');
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            const arr = b.sections;
            const i = arr.findIndex((s) => s.id === d.sectionId);
            if (i < 0) return;
            b.mode = 'canvas';
            arr.splice(i + 1, 0, {
              id: newSecId,
              name: 'Khu vực mới',
              anchorId: newSecId,
              height: 520,
              backgroundColor: '',
              blocks: [],
            });
          });
          setCanvasSelectedSectionId(newSecId);
          break;
        }
        case 'addSectionAtEnd': {
          const id = genLandingId('sec');
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            b.mode = 'canvas';
            b.sections.push({
              id,
              name: 'Khu vực mới',
              anchorId: id,
              height: 520,
              backgroundColor: '',
              blocks: [],
            });
          });
          setCanvasSelectedSectionId(id);
          break;
        }
        case 'selectBlock':
          if (d.sectionId) setCanvasSelectedSectionId(d.sectionId);
          if (d.blockId) setCanvasSelectedBlockId(d.blockId);
          break;
        case 'moveBlock': {
          const x = Number(d.x);
          const y = Number(d.y);
          if (!d.sectionId || !d.blockId || Number.isNaN(x) || Number.isNaN(y)) break;
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            const sec = b.sections.find((s) => s.id === d.sectionId);
            const blk = sec?.blocks.find((bl) => bl.id === d.blockId);
            if (!blk) return;
            const w = blk.w ?? 10;
            const h = blk.h ?? 10;
            blk.x = Math.max(0, Math.min(100 - w, x));
            blk.y = Math.max(0, Math.min(100 - h, y));
          });
          break;
        }
        case 'resizeBlock': {
          const x = Number(d.x);
          const y = Number(d.y);
          const w = Number(d.w);
          const h = Number(d.h);
          if (!d.sectionId || !d.blockId || [x, y, w, h].some((n) => Number.isNaN(n))) break;
          const skipUndo = d.skipUndo === true;
          const applyResize = (cfg: LandingConfig) => {
            const b = ensureLandingBuilder(cfg);
            const sec = b.sections.find((s) => s.id === d.sectionId);
            const blk = sec?.blocks.find((bl) => bl.id === d.blockId);
            if (!blk) return;
            const minPct = 1.5;
            let nw = Math.max(minPct, Math.min(100, w));
            let nh = Math.max(minPct, Math.min(100, h));
            let nx = Math.max(0, Math.min(100 - nw, x));
            let ny = Math.max(0, Math.min(100 - nh, y));
            if (nx + nw > 100) nw = Math.max(minPct, 100 - nx);
            if (ny + nh > 100) nh = Math.max(minPct, 100 - ny);
            blk.x = nx;
            blk.y = ny;
            blk.w = nw;
            blk.h = nh;
          };
          if (skipUndo) {
            setConfig((prev) => {
              const next = clone(prev);
              applyResize(next);
              return next;
            });
          } else {
            applyWithUndo(applyResize);
          }
          break;
        }
        case 'updateBlockContent': {
          if (!d.sectionId || !d.blockId) break;
          const html = typeof d.html === 'string' ? d.html : '';
          const plain = typeof d.plainFallback === 'string' ? d.plainFallback : '';
          const content = html.trim() ? html : plain;
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            const sec = b.sections.find((s) => s.id === d.sectionId);
            const blk = sec?.blocks.find((bl) => bl.id === d.blockId);
            if (!blk) return;
            if (blk.type === 'button') {
              blk.label = content.length ? content : blk.label;
            } else if (blk.type === 'shapeText') {
              blk.text = content.length ? content : blk.text;
            }
          });
          break;
        }
        case 'shapeTextDefaultColorOnly': {
          const sid = typeof d.sectionId === 'string' ? d.sectionId : '';
          const bid = typeof d.blockId === 'string' ? d.blockId : '';
          const hx = typeof (d as { hex?: string }).hex === 'string' ? (d as { hex?: string }).hex!.trim() : '';
          if (!sid || !bid || !hx) break;
          const nhx = normalizeHex(hx);
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            const sec = b.sections.find((s) => s.id === sid);
            const blk = sec?.blocks.find((bl) => bl.id === bid);
            if (blk && blk.type === 'shapeText') blk.style.color = nhx;
          });
          break;
        }
        case 'deleteBlock': {
          if (!d.sectionId || !d.blockId) break;
          deleteCanvasBlock(d.sectionId, d.blockId);
          break;
        }
        case 'copyBlock': {
          const sid = typeof d.sectionId === 'string' ? d.sectionId : '';
          const bid = typeof d.blockId === 'string' ? d.blockId : '';
          if (!sid || !bid) break;
          setConfig((prev) => {
            const c = clone(prev);
            const b = ensureLandingBuilder(c);
            const sec = b.sections.find((s) => s.id === sid);
            const blk = sec?.blocks.find((x) => x.id === bid);
            if (blk) landingBlockClipboardJsonRef.current = JSON.stringify(blk);
            return prev;
          });
          break;
        }
        case 'pasteBlock': {
          const raw = landingBlockClipboardJsonRef.current;
          const sid = typeof d.sectionId === 'string' ? d.sectionId : '';
          if (!raw || !sid) break;
          const afterId = typeof d.afterBlockId === 'string' ? d.afterBlockId : '';
          const newId = genLandingId('blk');
          applyWithUndo((cfg) => {
            let parsed: BuilderBlock;
            try {
              parsed = JSON.parse(raw) as BuilderBlock;
            } catch {
              return;
            }
            parsed.id = newId;
            parsed.x = Math.min(92, (parsed.x ?? 0) + 3);
            parsed.y = Math.min(92, (parsed.y ?? 0) + 3);
            const b = ensureLandingBuilder(cfg);
            const sec = b.sections.find((s) => s.id === sid);
            if (!sec) return;
            const idx = afterId ? sec.blocks.findIndex((x) => x.id === afterId) : -1;
            if (idx >= 0) sec.blocks.splice(idx + 1, 0, parsed);
            else sec.blocks.push(parsed);
          });
          setCanvasSelectedSectionId(sid);
          setCanvasSelectedBlockId(newId);
          break;
        }
        case 'duplicateBlock': {
          if (!d.sectionId || !d.blockId) break;
          const newId = genLandingId('blk');
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            const sec = b.sections.find((s) => s.id === d.sectionId);
            const blk = sec?.blocks.find((x) => x.id === d.blockId);
            if (!sec || !blk) return;
            const raw = JSON.parse(JSON.stringify(blk)) as BuilderBlock;
            raw.id = newId;
            raw.x = Math.min(92, (raw.x ?? 0) + 3);
            raw.y = Math.min(92, (raw.y ?? 0) + 3);
            sec.blocks.push(raw);
          });
          setCanvasSelectedSectionId(d.sectionId);
          setCanvasSelectedBlockId(newId);
          break;
        }
        case 'reorderBlock': {
          const sid = typeof d.sectionId === 'string' ? d.sectionId : '';
          const bid = typeof d.blockId === 'string' ? d.blockId : '';
          const mode = typeof d.mode === 'string' ? d.mode : '';
          if (!sid || !bid || !mode) break;
          applyWithUndo((cfg) => {
            const b = ensureLandingBuilder(cfg);
            const sec = b.sections.find((s) => s.id === sid);
            if (!sec) return;
            const arr = sec.blocks;
            const i = arr.findIndex((x) => x.id === bid);
            if (i < 0) return;
            if (mode === 'forward' && i < arr.length - 1) {
              const t = arr[i]!;
              arr[i] = arr[i + 1]!;
              arr[i + 1] = t;
            } else if (mode === 'backward' && i > 0) {
              const t2 = arr[i]!;
              arr[i] = arr[i - 1]!;
              arr[i - 1] = t2;
            } else if (mode === 'front') {
              const moved = arr.splice(i, 1)[0];
              if (moved) arr.push(moved);
            } else if (mode === 'back') {
              const moved2 = arr.splice(i, 1)[0];
              if (moved2) arr.unshift(moved2);
            }
          });
          break;
        }
        case 'dropLibraryMedia': {
          const url = typeof d.url === 'string' ? d.url.trim() : '';
          const secId = typeof d.sectionId === 'string' ? d.sectionId : '';
          const dx = Number(d.dropXPct);
          const dy = Number(d.dropYPct);
          const kd = d.kind;
          const libKind: LandingMediaLibraryKind =
            kd === 'video' || kd === 'gif' || kd === 'image' ? kd : 'image';
          if (!url || !secId || Number.isNaN(dx) || Number.isNaN(dy)) break;
          void applyCanvasLibraryAsset(url, libKind, {
            alwaysAdd: true,
            sectionId: secId,
            dropXPct: dx,
            dropYPct: dy,
          });
          break;
        }
        case 'requestUndo':
          undo();
          break;
        case 'requestRedo':
          redo();
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [applyCanvasLibraryAsset, applyWithUndo, builderSections, deleteCanvasBlock, redo, setConfig, undo]);

  useEffect(() => {
    if (editorMode !== 'canvas') return;
    const onWinKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el?.closest?.('input, textarea, [contenteditable="true"]')) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
        return;
      }
      if (e.key === 'Delete' && canvasSelectedBlockId && canvasSelectedSectionId) {
        e.preventDefault();
        deleteCanvasBlock(canvasSelectedSectionId, canvasSelectedBlockId);
      }
    };
    window.addEventListener('keydown', onWinKey, true);
    return () => window.removeEventListener('keydown', onWinKey, true);
  }, [editorMode, undo, redo, canvasSelectedBlockId, canvasSelectedSectionId, deleteCanvasBlock]);

  const handleUpload = async (file: File | null, onUrl: (url: string) => void) => {
    if (!file) return;
    const url = await uploadLandingAsset(file);
    if (url) onUrl(url);
  };

  const persistLandingPage = useCallback(async (): Promise<boolean> => {
    const body = clone(config) as LandingConfig;
    if (!body.builder || typeof body.builder !== 'object') body.builder = {};
    const bm = body.builder as Record<string, unknown>;
    bm.mode = editorMode === 'canvas' ? 'canvas' : 'legacy';

    setSaving(true);
    try {
      const res = await api.updateLandingPageConfig(body);
      const savedCfg = (res.config ?? body) as LandingConfig;
      applyFullConfig(savedCfg);
      const mode =
        (savedCfg.builder as { mode?: string } | undefined)?.mode === 'canvas' ? 'canvas' : 'legacy';
      setSavedSnapshot(buildPersistSnapshot(savedCfg, mode));
      undoPastRef.current = [];
      undoFutureRef.current = [];
      toast.success('Đã lưu');
      setIframeKey((k) => k + 1);
      return true;
    } catch (e) {
      console.error(e);
      toast.error('Lưu thất bại');
      return false;
    } finally {
      setSaving(false);
    }
  }, [config, editorMode, applyFullConfig, buildPersistSnapshot]);

  const handleSave = useCallback(() => {
    void persistLandingPage();
  }, [persistLandingPage]);

  const onBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    },
    [isDirty],
  );

  useBeforeUnload(onBeforeUnload);

  useEffect(() => {
    if (!isDirty) return;
    const onDocClickCapture = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;

      const el = (e.target as Element | null)?.closest?.('a[href]');
      if (!el || !(el instanceof HTMLAnchorElement)) return;
      if (el.target === '_blank' || el.download) return;

      const hrefAttr = el.getAttribute('href');
      if (!hrefAttr || hrefAttr.startsWith('#')) return;

      let url: URL;
      try {
        url = new URL(hrefAttr, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;

      const next = url.pathname + url.search + url.hash;
      const cur = window.location.pathname + window.location.search + window.location.hash;
      if (next === cur) return;

      e.preventDefault();
      e.stopPropagation();
      pendingNavigationRef.current = next;
      setLeavePromptOpen(true);
    };

    document.addEventListener('click', onDocClickCapture, true);
    return () => document.removeEventListener('click', onDocClickCapture, true);
  }, [isDirty]);

  /**
   * Sau Back một bước (đã gỡ entry guard), stack kiểu […, dashboard, landing, landing-trùng].
   * Chỉ navigate(-1) sẽ để lại 1 bản landing trong «tiến tới» — nút Forward bật oái oăm.
   * Lên lại đỉnh stack rồi go(-2) để về trang trước landing, gộp forward đúng một nhịp.
   */
  const exitHistoryAfterPopDialog = useCallback(() => {
    suppressLeavePopstateCountRef.current = 4;
    window.history.forward();
    window.setTimeout(() => {
      window.history.go(-2);
    }, 0);
  }, []);

  const handleLeaveStay = useCallback(() => {
    const pending = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setLeavePromptOpen(false);
    if (pending === 'pop') {
      suppressLeavePopstateCountRef.current = 1;
      window.history.forward();
      historyGuardPushedRef.current = true;
    }
  }, []);

  const handleLeaveDiscard = useCallback(() => {
    const pending = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setLeavePromptOpen(false);
    if (pending === 'pop') {
      exitHistoryAfterPopDialog();
      return;
    }
    if (pending) {
      suppressLeavePopstateCountRef.current = 1;
      navigate(pending);
    }
  }, [navigate, exitHistoryAfterPopDialog]);

  const handleLeaveSave = useCallback(async () => {
    const pending = pendingNavigationRef.current;
    const ok = await persistLandingPage();
    if (!ok) return;
    pendingNavigationRef.current = null;
    setLeavePromptOpen(false);
    if (pending === 'pop') {
      exitHistoryAfterPopDialog();
    } else if (pending) {
      suppressLeavePopstateCountRef.current = 1;
      navigate(pending);
    }
  }, [persistLandingPage, navigate, exitHistoryAfterPopDialog]);

  const copyPublicLink = () => {
    void navigator.clipboard.writeText(publicUrl);
    toast.success('Đã copy link công khai');
  };

  const g = (path: string[], fallback?: unknown): unknown => {
    let cur: unknown = config;
    for (const p of path) {
      if (cur == null || typeof cur !== 'object') return fallback;
      cur = (cur as Record<string, unknown>)[p];
    }
    return cur !== undefined ? cur : fallback;
  };

  const str = (path: string[], fb = '') => String(g(path, fb) ?? fb);

  const renderSettings = () => {
    switch (settingsTab) {
      case 'meta':
        return (
          <Stack spacing={2}>
            <TextField
              label="Tiêu đề trang (title)"
              fullWidth
              size="small"
              value={str(['meta', 'title'])}
              onChange={(e) => patch((d) => {
                if (!d.meta || typeof d.meta !== 'object') d.meta = {};
                (d.meta as Record<string, unknown>).title = e.target.value;
              })}
            />
            <TextField
              label="Mô tả (meta description)"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={str(['meta', 'description'])}
              onChange={(e) => patch((d) => {
                if (!d.meta || typeof d.meta !== 'object') d.meta = {};
                (d.meta as Record<string, unknown>).description = e.target.value;
              })}
            />
          </Stack>
        );
      case 'hero':
        return (
          <Stack spacing={2}>
            <Typography variant="caption" color="text.secondary">
              Video: mỗi dòng một URL (hoặc upload bên dưới — URL sẽ thêm vào cuối danh sách).
            </Typography>
            <TextField
              label="Danh sách URL video (mỗi dòng một URL)"
              fullWidth
              size="small"
              multiline
              minRows={4}
              value={Array.isArray((config.hero as Record<string, unknown>)?.videoUrls)
                ? ((config.hero as Record<string, unknown>).videoUrls as string[]).join('\n')
                : ''}
              onChange={(e) => patch((d) => {
                if (!d.hero || typeof d.hero !== 'object') d.hero = {};
                const lines = e.target.value.split('\n').map((s) => s.trim()).filter(Boolean);
                (d.hero as Record<string, unknown>).videoUrls = lines;
              })}
            />
            <Button
              variant="outlined"
              component="label"
              size="small"
              startIcon={<CloudUpload />}
              disabled={uploading || loading}
            >
              Upload video (thêm URL vào danh sách)
              <input
                type="file"
                hidden
                accept="video/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  void handleUpload(f ?? null, (url) => {
                    patch((d) => {
                      if (!d.hero || typeof d.hero !== 'object') d.hero = {};
                      const h = d.hero as Record<string, unknown>;
                      const arr = Array.isArray(h.videoUrls) ? [...(h.videoUrls as string[])] : [];
                      arr.push(url);
                      h.videoUrls = arr;
                    });
                  });
                  e.target.value = '';
                }}
              />
            </Button>
            <TextField
              label="Tiêu đề — phần trước highlight"
              fullWidth
              size="small"
              value={str(['hero', 'titleBefore'])}
              onChange={(e) => patch((d) => {
                if (!d.hero || typeof d.hero !== 'object') d.hero = {};
                (d.hero as Record<string, unknown>).titleBefore = e.target.value;
              })}
            />
            <TextField
              label="Tiêu đề — highlight (Memory)"
              fullWidth
              size="small"
              value={str(['hero', 'titleHighlight'])}
              onChange={(e) => patch((d) => {
                if (!d.hero || typeof d.hero !== 'object') d.hero = {};
                (d.hero as Record<string, unknown>).titleHighlight = e.target.value;
              })}
            />
            <TextField
              label="Tiêu đề — phần sau"
              fullWidth
              size="small"
              value={str(['hero', 'titleAfter'])}
              onChange={(e) => patch((d) => {
                if (!d.hero || typeof d.hero !== 'object') d.hero = {};
                (d.hero as Record<string, unknown>).titleAfter = e.target.value;
              })}
            />
            <TextField
              label="Subtitle"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={str(['hero', 'subtitle'])}
              onChange={(e) => patch((d) => {
                if (!d.hero || typeof d.hero !== 'object') d.hero = {};
                (d.hero as Record<string, unknown>).subtitle = e.target.value;
              })}
            />
            <Stack direction="row" spacing={1}>
              <TextField
                label="Nút cam — chữ"
                size="small"
                fullWidth
                value={str(['hero', 'ctaPrimary'])}
                onChange={(e) => patch((d) => {
                  if (!d.hero || typeof d.hero !== 'object') d.hero = {};
                  (d.hero as Record<string, unknown>).ctaPrimary = e.target.value;
                })}
              />
              <TextField
                label="Link (#anchor hoặc URL)"
                size="small"
                fullWidth
                value={str(['hero', 'ctaPrimaryHref'])}
                onChange={(e) => patch((d) => {
                  if (!d.hero || typeof d.hero !== 'object') d.hero = {};
                  (d.hero as Record<string, unknown>).ctaPrimaryHref = e.target.value;
                })}
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                label="Nút phụ — chữ"
                size="small"
                fullWidth
                value={str(['hero', 'ctaSecondary'])}
                onChange={(e) => patch((d) => {
                  if (!d.hero || typeof d.hero !== 'object') d.hero = {};
                  (d.hero as Record<string, unknown>).ctaSecondary = e.target.value;
                })}
              />
              <TextField
                label="Link"
                size="small"
                fullWidth
                value={str(['hero', 'ctaSecondaryHref'])}
                onChange={(e) => patch((d) => {
                  if (!d.hero || typeof d.hero !== 'object') d.hero = {};
                  (d.hero as Record<string, unknown>).ctaSecondaryHref = e.target.value;
                })}
              />
            </Stack>
            <TextField
              label="Thời gian chuyển video (ms)"
              type="number"
              size="small"
              fullWidth
              value={str(['hero', 'slideIntervalMs'], '6000')}
              onChange={(e) => patch((d) => {
                if (!d.hero || typeof d.hero !== 'object') d.hero = {};
                (d.hero as Record<string, unknown>).slideIntervalMs = Number(e.target.value) || 6000;
              })}
            />
          </Stack>
        );
      case 'partners': {
        const logos = (Array.isArray(g(['partners', 'logos'])) ? g(['partners', 'logos']) : []) as {
          name?: string;
          imageUrl?: string;
        }[];
        return (
          <Stack spacing={2}>
            <TextField
              label="Tiêu đề (phần trước brand)"
              fullWidth
              size="small"
              value={str(['partners', 'headingPrefix'])}
              onChange={(e) => patch((d) => {
                if (!d.partners || typeof d.partners !== 'object') d.partners = {};
                (d.partners as Record<string, unknown>).headingPrefix = e.target.value;
              })}
            />
            <TextField
              label="Tên brand (màu hồng)"
              fullWidth
              size="small"
              value={str(['partners', 'headingBrand'])}
              onChange={(e) => patch((d) => {
                if (!d.partners || typeof d.partners !== 'object') d.partners = {};
                (d.partners as Record<string, unknown>).headingBrand = e.target.value;
              })}
            />
            <Divider />
            <Typography variant="subtitle2">Logo đối tác</Typography>
            {logos.map((logo, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption">Logo {idx + 1}</Typography>
                    <IconButton
                      size="small"
                      aria-label="xóa"
                      onClick={() =>
                        patch((d) => {
                          if (!d.partners || typeof d.partners !== 'object') return;
                          const L = (d.partners as Record<string, unknown>).logos as unknown[];
                          L.splice(idx, 1);
                        })
                      }
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Stack>
                  <TextField
                    label="Tên"
                    size="small"
                    fullWidth
                    value={logo.name ?? ''}
                    onChange={(e) =>
                      patch((d) => {
                        if (!d.partners || typeof d.partners !== 'object') d.partners = {};
                        const L = (d.partners as Record<string, unknown>).logos as Record<string, unknown>[];
                        if (!L[idx]) L[idx] = {};
                        L[idx].name = e.target.value;
                      })
                    }
                  />
                  <TextField
                    label="URL ảnh"
                    size="small"
                    fullWidth
                    value={logo.imageUrl ?? ''}
                    onChange={(e) =>
                      patch((d) => {
                        if (!d.partners || typeof d.partners !== 'object') d.partners = {};
                        const L = (d.partners as Record<string, unknown>).logos as Record<string, unknown>[];
                        if (!L[idx]) L[idx] = {};
                        L[idx].imageUrl = e.target.value;
                      })
                    }
                  />
                  <Button size="small" variant="outlined" component="label" startIcon={<CloudUpload />} disabled={uploading}>
                    Upload ảnh logo
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        void handleUpload(f ?? null, (url) =>
                          patch((d) => {
                            if (!d.partners || typeof d.partners !== 'object') d.partners = {};
                            const L = (d.partners as Record<string, unknown>).logos as Record<string, unknown>[];
                            if (!L[idx]) L[idx] = {};
                            L[idx].imageUrl = url;
                          }),
                        );
                        e.target.value = '';
                      }}
                    />
                  </Button>
                </Stack>
              </Paper>
            ))}
            <Button
              size="small"
              startIcon={<Add />}
              variant="outlined"
              onClick={() =>
                patch((d) => {
                  if (!d.partners || typeof d.partners !== 'object') d.partners = {};
                  const L = ((d.partners as Record<string, unknown>).logos as unknown[]) ?? [];
                  L.push({ name: `Partner ${L.length + 1}`, imageUrl: '' });
                  (d.partners as Record<string, unknown>).logos = L;
                })
              }
            >
              Thêm logo
            </Button>
          </Stack>
        );
      }
      case 'frames':
        return (
          <Stack spacing={2}>
            <TextField
              label="Tiêu đề section"
              fullWidth
              size="small"
              value={str(['frames', 'title'])}
              onChange={(e) => patch((d) => {
                if (!d.frames || typeof d.frames !== 'object') d.frames = {};
                (d.frames as Record<string, unknown>).title = e.target.value;
              })}
            />
            <TextField
              label="Mô tả"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={str(['frames', 'description'])}
              onChange={(e) => patch((d) => {
                if (!d.frames || typeof d.frames !== 'object') d.frames = {};
                (d.frames as Record<string, unknown>).description = e.target.value;
              })}
            />
            <TextField
              label="Ảnh hàng trên (mỗi dòng một URL)"
              fullWidth
              size="small"
              multiline
              minRows={4}
              value={Array.isArray(g(['frames', 'topRowImages']))
                ? (g(['frames', 'topRowImages']) as string[]).join('\n')
                : ''}
              onChange={(e) => patch((d) => {
                if (!d.frames || typeof d.frames !== 'object') d.frames = {};
                const lines = e.target.value.split('\n').map((s) => s.trim()).filter(Boolean);
                (d.frames as Record<string, unknown>).topRowImages = lines;
              })}
            />
            <Button variant="outlined" size="small" component="label" startIcon={<CloudUpload />} disabled={uploading}>
              Upload ảnh → thêm vào hàng trên
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  void handleUpload(f ?? null, (url) =>
                    patch((d) => {
                      if (!d.frames || typeof d.frames !== 'object') d.frames = {};
                      const fr = d.frames as Record<string, unknown>;
                      const arr = Array.isArray(fr.topRowImages) ? [...(fr.topRowImages as string[])] : [];
                      arr.push(url);
                      fr.topRowImages = arr;
                    }),
                  );
                  e.target.value = '';
                }}
              />
            </Button>
            <TextField
              label="Ảnh hàng dưới (mỗi dòng một URL)"
              fullWidth
              size="small"
              multiline
              minRows={4}
              value={Array.isArray(g(['frames', 'bottomRowImages']))
                ? (g(['frames', 'bottomRowImages']) as string[]).join('\n')
                : ''}
              onChange={(e) => patch((d) => {
                if (!d.frames || typeof d.frames !== 'object') d.frames = {};
                const lines = e.target.value.split('\n').map((s) => s.trim()).filter(Boolean);
                (d.frames as Record<string, unknown>).bottomRowImages = lines;
              })}
            />
            <Button variant="outlined" size="small" component="label" startIcon={<CloudUpload />} disabled={uploading}>
              Upload ảnh → thêm vào hàng dưới
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  void handleUpload(f ?? null, (url) =>
                    patch((d) => {
                      if (!d.frames || typeof d.frames !== 'object') d.frames = {};
                      const fr = d.frames as Record<string, unknown>;
                      const arr = Array.isArray(fr.bottomRowImages) ? [...(fr.bottomRowImages as string[])] : [];
                      arr.push(url);
                      fr.bottomRowImages = arr;
                    }),
                  );
                  e.target.value = '';
                }}
              />
            </Button>
            <TextField
              label="Tốc độ slide (giây)"
              type="number"
              size="small"
              fullWidth
              value={str(['frames', 'slideDurationSec'], '18')}
              onChange={(e) => patch((d) => {
                if (!d.frames || typeof d.frames !== 'object') d.frames = {};
                (d.frames as Record<string, unknown>).slideDurationSec = Number(e.target.value) || 18;
              })}
            />
          </Stack>
        );
      case 'process': {
        const steps = (Array.isArray(g(['process', 'steps'])) ? g(['process', 'steps']) : []) as {
          title?: string;
          description?: string;
        }[];
        const padded = [...steps];
        while (padded.length < 3) padded.push({});
        return (
          <Stack spacing={2}>
            <TextField
              label="Badge"
              size="small"
              fullWidth
              value={str(['process', 'badge'])}
              onChange={(e) => patch((d) => {
                if (!d.process || typeof d.process !== 'object') d.process = {};
                (d.process as Record<string, unknown>).badge = e.target.value;
              })}
            />
            <TextField
              label="Tiêu đề"
              size="small"
              fullWidth
              value={str(['process', 'title'])}
              onChange={(e) => patch((d) => {
                if (!d.process || typeof d.process !== 'object') d.process = {};
                (d.process as Record<string, unknown>).title = e.target.value;
              })}
            />
            <TextField
              label="Mô tả"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={str(['process', 'description'])}
              onChange={(e) => patch((d) => {
                if (!d.process || typeof d.process !== 'object') d.process = {};
                (d.process as Record<string, unknown>).description = e.target.value;
              })}
            />
            {[0, 1, 2].map((i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Bước {i + 1}
                </Typography>
                <TextField
                  label="Tiêu đề bước"
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  value={padded[i]?.title ?? ''}
                  onChange={(e) =>
                    patch((d) => {
                      if (!d.process || typeof d.process !== 'object') d.process = {};
                      const st = ([...(Array.isArray((d.process as Record<string, unknown>).steps)
                        ? ((d.process as Record<string, unknown>).steps as unknown[])
                        : [])]) as Record<string, unknown>[];
                      while (st.length <= i) st.push({});
                      st[i] = { ...(st[i] as object), title: e.target.value };
                      (d.process as Record<string, unknown>).steps = st;
                    })
                  }
                />
                <TextField
                  label="Mô tả"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  sx={{ mt: 1 }}
                  value={padded[i]?.description ?? ''}
                  onChange={(e) =>
                    patch((d) => {
                      if (!d.process || typeof d.process !== 'object') d.process = {};
                      const st = ([...(Array.isArray((d.process as Record<string, unknown>).steps)
                        ? ((d.process as Record<string, unknown>).steps as unknown[])
                        : [])]) as Record<string, unknown>[];
                      while (st.length <= i) st.push({});
                      st[i] = { ...(st[i] as object), description: e.target.value };
                      (d.process as Record<string, unknown>).steps = st;
                    })
                  }
                />
              </Paper>
            ))}
          </Stack>
        );
      }
      case 'pricing': {
        const plans = (Array.isArray(g(['pricing', 'plans'])) ? g(['pricing', 'plans']) : []) as Record<
          string,
          unknown
        >[];
        const pad = [...plans];
        while (pad.length < 3) pad.push({});
        return (
          <Stack spacing={2}>
            <TextField
              label="Tiêu đề section"
              size="small"
              fullWidth
              value={str(['pricing', 'title'])}
              onChange={(e) => patch((d) => {
                if (!d.pricing || typeof d.pricing !== 'object') d.pricing = {};
                (d.pricing as Record<string, unknown>).title = e.target.value;
              })}
            />
            <TextField
              label="Mô tả"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={str(['pricing', 'description'])}
              onChange={(e) => patch((d) => {
                if (!d.pricing || typeof d.pricing !== 'object') d.pricing = {};
                (d.pricing as Record<string, unknown>).description = e.target.value;
              })}
            />
            {[0, 1, 2].map((i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Gói {i + 1}
                </Typography>
                <Stack spacing={1}>
                  <TextField
                    label="Tên máy"
                    size="small"
                    fullWidth
                    value={String(pad[i]?.name ?? '')}
                    onChange={(e) =>
                      patch((d) => {
                        if (!d.pricing || typeof d.pricing !== 'object') d.pricing = {};
                        const pl = ([...(Array.isArray((d.pricing as Record<string, unknown>).plans)
                          ? ((d.pricing as Record<string, unknown>).plans as unknown[])
                          : [])]) as Record<string, unknown>[];
                        while (pl.length <= i) pl.push({});
                        pl[i] = { ...pl[i], name: e.target.value };
                        (d.pricing as Record<string, unknown>).plans = pl;
                      })
                    }
                  />
                  <TextField
                    label="URL ảnh máy"
                    size="small"
                    fullWidth
                    value={String(pad[i]?.imageUrl ?? '')}
                    onChange={(e) =>
                      patch((d) => {
                        if (!d.pricing || typeof d.pricing !== 'object') d.pricing = {};
                        const pl = ([...(Array.isArray((d.pricing as Record<string, unknown>).plans)
                          ? ((d.pricing as Record<string, unknown>).plans as unknown[])
                          : [])]) as Record<string, unknown>[];
                        while (pl.length <= i) pl.push({});
                        pl[i] = { ...pl[i], imageUrl: e.target.value };
                        (d.pricing as Record<string, unknown>).plans = pl;
                      })
                    }
                  />
                  <Button size="small" variant="outlined" component="label" startIcon={<CloudUpload />} disabled={uploading}>
                    Upload ảnh máy
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        void handleUpload(f ?? null, (url) =>
                          patch((d) => {
                            if (!d.pricing || typeof d.pricing !== 'object') d.pricing = {};
                            const pl = ([...(Array.isArray((d.pricing as Record<string, unknown>).plans)
                              ? ((d.pricing as Record<string, unknown>).plans as unknown[])
                              : [])]) as Record<string, unknown>[];
                            while (pl.length <= i) pl.push({});
                            pl[i] = { ...pl[i], imageUrl: url };
                            (d.pricing as Record<string, unknown>).plans = pl;
                          }),
                        );
                        e.target.value = '';
                      }}
                    />
                  </Button>
                  <TextField
                    label="Giá hiển thị"
                    size="small"
                    fullWidth
                    value={String(pad[i]?.priceLabel ?? '')}
                    onChange={(e) =>
                      patch((d) => {
                        if (!d.pricing || typeof d.pricing !== 'object') d.pricing = {};
                        const pl = ([...(Array.isArray((d.pricing as Record<string, unknown>).plans)
                          ? ((d.pricing as Record<string, unknown>).plans as unknown[])
                          : [])]) as Record<string, unknown>[];
                        while (pl.length <= i) pl.push({});
                        pl[i] = { ...pl[i], priceLabel: e.target.value };
                        (d.pricing as Record<string, unknown>).plans = pl;
                      })
                    }
                  />
                  <TextField
                    label="Nổi bật (true/false)"
                    size="small"
                    fullWidth
                    value={String(pad[i]?.recommended ?? false)}
                    onChange={(e) =>
                      patch((d) => {
                        if (!d.pricing || typeof d.pricing !== 'object') d.pricing = {};
                        const pl = ([...(Array.isArray((d.pricing as Record<string, unknown>).plans)
                          ? ((d.pricing as Record<string, unknown>).plans as unknown[])
                          : [])]) as Record<string, unknown>[];
                        while (pl.length <= i) pl.push({});
                        pl[i] = { ...pl[i], recommended: e.target.value === 'true' };
                        (d.pricing as Record<string, unknown>).plans = pl;
                      })
                    }
                  />
                  <TextField
                    label="Thông số (JSON array {label,value})"
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    value={JSON.stringify(pad[i]?.specs ?? [], null, 2)}
                    onChange={(e) =>
                      patch((d) => {
                        try {
                          const specs = JSON.parse(e.target.value);
                          if (!Array.isArray(specs)) return;
                          if (!d.pricing || typeof d.pricing !== 'object') d.pricing = {};
                          const pl = ([...(Array.isArray((d.pricing as Record<string, unknown>).plans)
                            ? ((d.pricing as Record<string, unknown>).plans as unknown[])
                            : [])]) as Record<string, unknown>[];
                          while (pl.length <= i) pl.push({});
                          pl[i] = { ...pl[i], specs };
                          (d.pricing as Record<string, unknown>).plans = pl;
                        } catch {
                          /* ignore parse */
                        }
                      })
                    }
                  />
                </Stack>
              </Paper>
            ))}
          </Stack>
        );
      }
      case 'events': {
        const cards = (Array.isArray(g(['events', 'cards'])) ? g(['events', 'cards']) : []) as Record<
          string,
          unknown
        >[];
        return (
          <Stack spacing={2}>
            <TextField
              label="Badge"
              size="small"
              fullWidth
              value={str(['events', 'badge'])}
              onChange={(e) => patch((d) => {
                if (!d.events || typeof d.events !== 'object') d.events = {};
                (d.events as Record<string, unknown>).badge = e.target.value;
              })}
            />
            <TextField
              label="Tiêu đề"
              size="small"
              fullWidth
              value={str(['events', 'title'])}
              onChange={(e) => patch((d) => {
                if (!d.events || typeof d.events !== 'object') d.events = {};
                (d.events as Record<string, unknown>).title = e.target.value;
              })}
            />
            <TextField
              label="Mô tả phụ"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={str(['events', 'description'])}
              onChange={(e) => patch((d) => {
                if (!d.events || typeof d.events !== 'object') d.events = {};
                (d.events as Record<string, unknown>).description = e.target.value;
              })}
            />
            <Divider />
            <Button
              size="small"
              startIcon={<Add />}
              variant="outlined"
              onClick={() =>
                patch((d) => {
                  if (!d.events || typeof d.events !== 'object') d.events = {};
                  const c = ((d.events as Record<string, unknown>).cards as unknown[]) ?? [];
                  c.push({ title: 'Sự kiện mới', location: '', coverImage: '', galleryImages: [] });
                  (d.events as Record<string, unknown>).cards = c;
                })
              }
            >
              Thêm sự kiện
            </Button>
            {cards.map((card, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption">Sự kiện {idx + 1}</Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        patch((d) => {
                          if (!d.events || typeof d.events !== 'object') return;
                          const c = (d.events as Record<string, unknown>).cards as unknown[];
                          c.splice(idx, 1);
                        })
                      }
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Stack>
                  <TextField
                    label="Tên"
                    size="small"
                    fullWidth
                    value={String(card.title ?? '')}
                    onChange={(e) =>
                      patch((d) => {
                        if (!d.events || typeof d.events !== 'object') d.events = {};
                        const c = (d.events as Record<string, unknown>).cards as Record<string, unknown>[];
                        if (!c[idx]) c[idx] = {};
                        c[idx].title = e.target.value;
                      })
                    }
                  />
                  <TextField
                    label="Địa điểm"
                    size="small"
                    fullWidth
                    value={String(card.location ?? '')}
                    onChange={(e) =>
                      patch((d) => {
                        if (!d.events || typeof d.events !== 'object') d.events = {};
                        const c = (d.events as Record<string, unknown>).cards as Record<string, unknown>[];
                        if (!c[idx]) c[idx] = {};
                        c[idx].location = e.target.value;
                      })
                    }
                  />
                  <TextField
                    label="Ảnh bìa (URL)"
                    size="small"
                    fullWidth
                    value={String(card.coverImage ?? '')}
                    onChange={(e) =>
                      patch((d) => {
                        if (!d.events || typeof d.events !== 'object') d.events = {};
                        const c = (d.events as Record<string, unknown>).cards as Record<string, unknown>[];
                        if (!c[idx]) c[idx] = {};
                        c[idx].coverImage = e.target.value;
                      })
                    }
                  />
                  <Button size="small" variant="outlined" component="label" startIcon={<CloudUpload />} disabled={uploading}>
                    Upload ảnh bìa
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        void handleUpload(f ?? null, (url) =>
                          patch((d) => {
                            if (!d.events || typeof d.events !== 'object') d.events = {};
                            const c = (d.events as Record<string, unknown>).cards as Record<string, unknown>[];
                            if (!c[idx]) c[idx] = {};
                            c[idx].coverImage = url;
                            const gal = Array.isArray(c[idx].galleryImages) ? [...(c[idx].galleryImages as string[])] : [];
                            if (!gal.includes(url)) gal.push(url);
                            c[idx].galleryImages = gal;
                          }),
                        );
                        e.target.value = '';
                      }}
                    />
                  </Button>
                  <TextField
                    label="Gallery (mỗi dòng một URL)"
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    value={Array.isArray(card.galleryImages) ? (card.galleryImages as string[]).join('\n') : ''}
                    onChange={(e) =>
                      patch((d) => {
                        if (!d.events || typeof d.events !== 'object') d.events = {};
                        const c = (d.events as Record<string, unknown>).cards as Record<string, unknown>[];
                        if (!c[idx]) c[idx] = {};
                        const lines = e.target.value.split('\n').map((s) => s.trim()).filter(Boolean);
                        c[idx].galleryImages = lines;
                      })
                    }
                  />
                </Stack>
              </Paper>
            ))}
          </Stack>
        );
      }
      case 'cta':
        return (
          <Stack spacing={2}>
            <TextField
              label="Tiêu đề CTA"
              size="small"
              fullWidth
              value={str(['cta', 'title'])}
              onChange={(e) => patch((d) => {
                if (!d.cta || typeof d.cta !== 'object') d.cta = {};
                (d.cta as Record<string, unknown>).title = e.target.value;
              })}
            />
            <TextField
              label="Mô tả"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={str(['cta', 'description'])}
              onChange={(e) => patch((d) => {
                if (!d.cta || typeof d.cta !== 'object') d.cta = {};
                (d.cta as Record<string, unknown>).description = e.target.value;
              })}
            />
            <TextField
              label="Chữ nút"
              size="small"
              fullWidth
              value={str(['cta', 'buttonText'])}
              onChange={(e) => patch((d) => {
                if (!d.cta || typeof d.cta !== 'object') d.cta = {};
                (d.cta as Record<string, unknown>).buttonText = e.target.value;
              })}
            />
            <TextField
              label="Link nút"
              size="small"
              fullWidth
              value={str(['cta', 'buttonHref'])}
              onChange={(e) => patch((d) => {
                if (!d.cta || typeof d.cta !== 'object') d.cta = {};
                (d.cta as Record<string, unknown>).buttonHref = e.target.value;
              })}
            />
            <TextField
              label="Footer (copyright)"
              size="small"
              fullWidth
              value={str(['footerNote'])}
              onChange={(e) => patch((d) => {
                d.footerNote = e.target.value;
              })}
            />
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, py: 2, maxWidth: '100%' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {a.landing.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {a.landing.subtitle}
      </Typography>

      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={editorMode}
        onChange={(_, v) => {
          if (v) setEditorMode(v);
        }}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="legacy">{a.landing.classicForm}</ToggleButton>
        <ToggleButton value="canvas">{a.landing.canvasMode}</ToggleButton>
      </ToggleButtonGroup>

      <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
        <strong>{a.landing.publicLabel}</strong>{' '}
        <Box component="span" sx={{ wordBreak: 'break-all', fontSize: 13 }}>
          {publicUrl}
        </Box>
        <Tooltip title={a.landing.copy}>
          <IconButton size="small" onClick={copyPublicLink}>
            <ContentCopy fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button size="small" href={publicUrl} target="_blank" rel="noreferrer" endIcon={<OpenInNew />}>
          {a.landing.open}
        </Button>
      </Alert>

      <Grid container spacing={2} alignItems="stretch">
        {/* Cột trái — chỉ form cổ điển; canvas dùng panel giữa rail & preview */}
        {editorMode !== 'canvas' && (
        <Grid item xs={12} lg={4} sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Paper
            sx={{
              p: 2,
              height: '100%',
              minHeight: { lg: 'calc(100vh - 200px)' },
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
            variant="outlined"
          >
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              {a.landing.settingsTitle}
            </Typography>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 0 },
                minHeight: 0,
                mb: 2,
              }}
            >
              <Tabs
                orientation="vertical"
                value={settingsTab}
                onChange={(_, v) => setSettingsTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  flexShrink: 0,
                  borderRight: { sm: 1 },
                  borderBottom: { xs: 1, sm: 0 },
                  borderColor: 'divider',
                  minHeight: { xs: 'auto', sm: 200 },
                  '& .MuiTabs-indicator': { left: 0, right: 'auto', width: 3 },
                  '& .MuiTab-root': {
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    fontSize: 12,
                    minHeight: 40,
                    py: 1,
                    px: 1,
                  },
                }}
              >
                {SECTION_TABS.map((t) => (
                  <Tab key={t.value} label={t.label} value={t.value} />
                ))}
              </Tabs>
              <Box sx={{ flex: 1, overflow: 'auto', minWidth: 0, pl: { sm: 1.5 }, pt: { xs: 1, sm: 0 } }}>
                {loading ? <Typography>{a.landing.loading}</Typography> : renderSettings()}
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button variant="outlined" startIcon={<Refresh />} onClick={load} disabled={loading} size="small">
                {a.landing.reload}
              </Button>
            </Stack>
          </Paper>
        </Grid>
        )}

        {/* Live preview — canvas: full width hàng dưới; legacy: 8/12 */}
        <Grid item xs={12} lg={editorMode === 'canvas' ? 12 : 8} sx={{ minWidth: 0, display: 'flex' }}>
          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              minWidth: 0,
              height: '100%',
              minHeight: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 170px)', lg: 'calc(100vh - 120px)' },
              display: 'flex',
              flexDirection: editorMode === 'canvas' ? 'row' : 'column',
              alignItems: 'stretch',
              overflow: 'hidden',
              p: 0,
            }}
          >
            {editorMode === 'canvas' && (
              <>
                <LandingCanvasLeftRail
                  highlightId={canvasRailHighlightId}
                  onGo={(id) => {
                    if (id === LANDING_CANVAS_SCROLL_IDS.detail) {
                      toggleCanvasChiTietPanel();
                      return;
                    }
                    let panel: CanvasRailPanel | undefined;
                    if (id === LANDING_CANVAS_SCROLL_IDS.library) panel = 'library';
                    else if (id === LANDING_CANVAS_SCROLL_IDS.addButton) panel = 'addButton';
                    else if (id === LANDING_CANVAS_SCROLL_IDS.addShape) panel = 'addShape';
                    else if (id === LANDING_CANVAS_SCROLL_IDS.addEventList) panel = 'addEventList';
                    else if (id === LANDING_CANVAS_SCROLL_IDS.addImageSlider) panel = 'addImageSlider';

                    if (panel) {
                      const p: CanvasRailPanel = panel;
                      setCanvasChiTietOpen(false);
                      setCanvasRailHighlightId(id);
                      setCanvasRailPanel((prev) => (prev === p ? null : p));
                      return;
                    }
                    setCanvasRailPanel(null);
                    setCanvasRailHighlightId(id);
                    if (
                      id === LANDING_CANVAS_SCROLL_IDS.page ||
                      id === LANDING_CANVAS_SCROLL_IDS.detail
                    ) {
                      setCanvasChiTietOpen(true);
                    }
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      });
                    });
                  }}
                  disableWithoutSection={!canvasSelectedSectionId}
                  disableDetail={!canvasSelectedSectionId}
                />
                <Slide in={canvasChiTietOpen} direction="right" mountOnEnter unmountOnExit>
                  <Box sx={CANVAS_CHI_TIET_PANEL_BOX_SX}>
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{ mb: 1, flexShrink: 0 }}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>
                        Xem chi tiết
                      </Typography>
                      <Tooltip title="Đóng">
                        <IconButton size="small" aria-label="Đóng Xem chi tiết" onClick={closeCanvasChiTietPanel}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <LandingPageCanvasEditor
                        loading={loading}
                        config={config}
                        patch={patch}
                        selectedSectionId={canvasSelectedSectionId}
                        selectedBlockId={canvasSelectedBlockId}
                        onSelectedBlockIdChange={setCanvasSelectedBlockId}
                      />
                    </Box>
                    <Divider sx={{ my: 1.5, flexShrink: 0 }} />
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ flexShrink: 0 }}>
                      <Button variant="outlined" startIcon={<Refresh />} onClick={load} disabled={loading} size="small">
                        {a.landing.reload}
                      </Button>
                    </Stack>
                  </Box>
                </Slide>
                <Slide in={canvasRailPanel !== null} direction="right" mountOnEnter unmountOnExit>
                  <Box sx={CANVAS_RAIL_PANEL_BOX_SX}>
                    {canvasRailPanel === 'library' && (
                      <>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                          Thư viện — tải lên & chọn
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                          Ảnh, video, GIF dùng chung. Bấm một file để thêm lên trang; nếu đang chọn khối ảnh/video thì sẽ thay nội dung khối đó.
                        </Typography>
                        <LandingMediaLibraryPicker
                          alignTabToMediaKind={
                            previewToolbarBlock?.type === 'media' || previewToolbarBlock?.type === 'imageSlider'
                          }
                          disabled={uploading || loading}
                          uploadAsset={uploadLandingAsset}
                          mediaKind={
                            previewToolbarBlock?.type === 'media' && previewToolbarBlock.mediaKind === 'video'
                              ? 'video'
                              : 'image'
                          }
                          onApply={applyCanvasLibraryAsset}
                          onDragReleaseOverPreview={handleLibraryDragReleaseOverPreview}
                          onLibraryDragArmChange={postLibraryDragArmToPreview}
                        />
                      </>
                    )}
                    {canvasRailPanel === 'addButton' && (
                      <>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                          Nút bấm
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                          Thêm nút trên canvas; chỉnh chữ, màu và hành động trong panel <strong>Chi tiết</strong> hoặc thanh công cụ trên preview.
                        </Typography>
                        <Button
                          fullWidth
                          variant="contained"
                          size="medium"
                          startIcon={<Add />}
                          disabled={!canvasSelectedSectionId || loading}
                          onClick={() => addCanvasBlock('button')}
                          sx={{ py: 1, fontWeight: 600 }}
                        >
                          Thêm nút vào trang
                        </Button>
                      </>
                    )}
                    {canvasRailPanel === 'addShape' && (
                      <>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                          Văn bản
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                          Chọn kiểu gợi ý — chỉnh font và nội dung trong panel <strong>Chi tiết</strong>.
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color="text.secondary"
                          sx={{ display: 'block', mb: 1, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: 10 }}
                        >
                          Kiểu văn bản gợi ý
                        </Typography>
                        <Stack spacing={1.25}>
                          <ButtonBase
                            focusRipple
                            disabled={!canvasSelectedSectionId || loading}
                            onClick={() => addCanvasBlock('shapeText', 'heading')}
                            sx={{
                              width: '100%',
                              display: 'block',
                              textAlign: 'left',
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              p: 1.5,
                              bgcolor: 'grey.50',
                              transition: 'background-color 0.15s',
                              '&:hover': { bgcolor: 'action.hover' },
                              '&.Mui-disabled': { opacity: 0.5 },
                            }}
                          >
                            <Typography sx={{ fontSize: '1.65rem', fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }}>
                              Tiêu đề
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              Thêm tiêu đề
                            </Typography>
                          </ButtonBase>
                          <ButtonBase
                            focusRipple
                            disabled={!canvasSelectedSectionId || loading}
                            onClick={() => addCanvasBlock('shapeText', 'subheading')}
                            sx={{
                              width: '100%',
                              display: 'block',
                              textAlign: 'left',
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              p: 1.5,
                              bgcolor: 'grey.50',
                              transition: 'background-color 0.15s',
                              '&:hover': { bgcolor: 'action.hover' },
                              '&.Mui-disabled': { opacity: 0.5 },
                            }}
                          >
                            <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.25, color: 'text.primary' }}>
                              Tiêu đề phụ
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              Thêm tiêu đề phụ
                            </Typography>
                          </ButtonBase>
                          <ButtonBase
                            focusRipple
                            disabled={!canvasSelectedSectionId || loading}
                            onClick={() => addCanvasBlock('shapeText', 'body')}
                            sx={{
                              width: '100%',
                              display: 'block',
                              textAlign: 'left',
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              p: 1.5,
                              bgcolor: 'grey.50',
                              transition: 'background-color 0.15s',
                              '&:hover': { bgcolor: 'action.hover' },
                              '&.Mui-disabled': { opacity: 0.5 },
                            }}
                          >
                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 400, lineHeight: 1.45, color: 'text.primary' }}>
                              Thêm nội dung văn bản — đoạn văn ngắn hoặc mô tả.
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              Nội dung
                            </Typography>
                          </ButtonBase>
                        </Stack>
                      </>
                    )}
                    {canvasRailPanel === 'addEventList' && (
                      <>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                          Danh sách sự kiện
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                          Lưới thẻ sự kiện; chỉnh vị trí và số thẻ mỗi lượt trong <strong>Chi tiết</strong>.
                        </Typography>
                        <Button
                          fullWidth
                          variant="contained"
                          size="medium"
                          startIcon={<Add />}
                          disabled={!canvasSelectedSectionId || loading}
                          onClick={() => addCanvasBlock('eventList')}
                          sx={{ py: 1, fontWeight: 600 }}
                        >
                          Thêm danh sách sự kiện
                        </Button>
                      </>
                    )}
                    {canvasRailPanel === 'addImageSlider' && (
                      <Box id={LANDING_CANVAS_SCROLL_IDS.addImageSlider}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                          Dải ảnh cuộn
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                          Các ảnh cuộn ngang liên tục. Chuột phải trên preview để đổi chiều / tốc độ; ô + hoặc kéo ảnh từ máy để thêm.
                        </Typography>
                        <Button
                          fullWidth
                          variant="contained"
                          size="medium"
                          startIcon={<Add />}
                          disabled={!canvasSelectedSectionId || loading}
                          onClick={() => addCanvasBlock('imageSlider')}
                          sx={{ py: 1, fontWeight: 600 }}
                        >
                          Thêm dải ảnh cuộn
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Slide>
              </>
            )}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                p: 2,
              }}
            >
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Live preview
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mb: 1 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="pv-sel">Phần hiển thị</InputLabel>
                <Select
                  labelId="pv-sel"
                  label="Phần hiển thị"
                  value={previewSection}
                  onChange={(e) => setPreviewSection(e.target.value)}
                >
                  {previewSectionOptions.map((o) => (
                    <MenuItem key={o.id || 'full'} value={o.id}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {editorMode === 'canvas' && (
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={previewPagesMode ? 'pages' : 'scroll'}
                  onChange={(_, v) => {
                    if (v != null) {
                      setPreviewPagesMode(v === 'pages');
                      setIframeKey((k) => k + 1);
                    }
                  }}
                  aria-label="Kiểu xem preview"
                >
                  <ToggleButton value="scroll">Toàn trang</ToggleButton>
                  <ToggleButton value="pages">Từng trang</ToggleButton>
                </ToggleButtonGroup>
              )}
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={loading || saving || !isDirty}
                size="small"
              >
                {saving ? a.landing.saving : a.landing.saveConfig}
              </Button>
            </Stack>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'visible',
                minHeight: { xs: 700, sm: 760, md: 860 },
                height: { xs: '74vh', sm: '78vh', md: 'calc(100vh - 112px)' },
                maxHeight: { xs: 'none', md: 'min(100vh - 64px, 1600px)' },
                bgcolor: 'action.hover',
              }}
            >
              {editorMode === 'canvas' && canvasSelectedSectionId && (
                <Paper
                  elevation={0}
                  sx={{
                    flexShrink: 0,
                    px: 1.25,
                    py: 1,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: '0 8px 28px rgba(15, 23, 42, 0.14)',
                  }}
                >
                  {previewToolbarBlock ? (
                    <CanvasBlockToolbar
                      block={previewToolbarBlock}
                      sectionId={canvasSelectedSectionId}
                      patch={patch}
                      onBlockDuplicated={setCanvasSelectedBlockId}
                      onBlockDeleted={() => setCanvasSelectedBlockId(null)}
                      onOpenChiTiet={toggleCanvasChiTietPanel}
                      onRequestShapeTextToolbarColor={requestShapeTextToolbarColor}
                    />
                  ) : (
                    <CanvasPageToolbar onOpenChiTiet={toggleCanvasChiTietPanel} />
                  )}
                </Paper>
              )}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  bgcolor: 'grey.900',
                  position: 'relative',
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box
                  component="iframe"
                  ref={iframeRef}
                  key={iframeKey}
                  title="Landing preview"
                  src={previewSrc}
                  onLoad={pushPreviewToIframe}
                  sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: { xs: 620, md: 720 },
                    border: 0,
                    display: 'block',
                  }}
                />
              </Box>
            </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={sliderSlotDialog !== null}
        onClose={() => {
          landingSliderSlotPickRef.current = null;
          setSliderSlotDialog(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Chọn ảnh từ thư viện</DialogTitle>
        <DialogContent sx={{ pt: 1, maxHeight: '75vh', overflow: 'auto' }}>
          <LandingMediaLibraryPicker
            key={
              sliderSlotDialog
                ? `${sliderSlotDialog.sectionId}-${sliderSlotDialog.blockId}-${sliderSlotDialog.slotIndex}`
                : 'slider-slot-closed'
            }
            alignTabToMediaKind
            pickMode="multiSelect"
            disabled={uploading || loading}
            uploadAsset={uploadLandingAsset}
            mediaKind="image"
            onApply={noopLibraryApply}
            onApplyMulti={applySliderSlotLibraryMulti}
            onDragReleaseOverPreview={handleLibraryDragReleaseOverPreview}
            onLibraryDragArmChange={postLibraryDragArmToPreview}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => {
              landingSliderSlotPickRef.current = null;
              setSliderSlotDialog(null);
            }}
          >
            {t.common.close}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={leavePromptOpen} onClose={handleLeaveStay} maxWidth="xs" fullWidth>
        <DialogTitle>{a.landing.unsavedTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{a.landing.unsavedBody}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={handleLeaveStay}>{a.landing.stay}</Button>
          <Button color="warning" variant="outlined" onClick={handleLeaveDiscard}>
            {a.landing.leaveWithoutSave}
          </Button>
          <Button variant="contained" onClick={() => void handleLeaveSave()} disabled={saving}>
            {saving ? a.landing.saving : a.landing.saveAndLeave}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminLandingPage;
